export interface CategoryGeneratorState {
	id: number;
	name: string;
	available_questions: number;
	available_questions_1: number;
	available_questions_2: number;
	available_questions_3: number;
	levels: number[];
	invalid?: boolean;
	maximum?: number;
	question_count: number | null;
	/**
	 * Configurator selection mode, persisted with the session state so a
	 * restore is lossless (an empty subcategory-mode pick and a category-mode
	 * pick with no count serialize identically otherwise). Never sent to the
	 * API — the cart transform builds explicit payload literals.
	 */
	mode?: 'category' | 'subcategory';
	subcategories: SubcategoryGeneratorState[];
}

export interface SubcategoryGeneratorState {
	id: number;
	name: string;
	available_questions: number;
	available_questions_1: number;
	available_questions_2: number;
	available_questions_3: number;
	levels?: number[];
	question_count?: number | null;
	invalid?: boolean;
	maximum?: number;
}

export interface CategoryFetchResponse {
	id: number;
	name: string;
	available_questions: number;
	available_questions_1: number;
	available_questions_2: number;
	available_questions_3: number;
	subcategories: SubcategoryFetchResponse[];
}

interface SubcategoryFetchResponse {
	id: number;
	name: string;
	available_questions: number;
	available_questions_1: number;
	available_questions_2: number;
	available_questions_3: number;
}
export class CategoryFetchResponse {
	constructor(object?: Partial<CategoryFetchResponse>) {
		this.id = object?.id || 0;
		this.name = object?.name || '';
		this.available_questions = object?.available_questions || 0;
		this.available_questions_1 = object?.available_questions_1 || 0;
		this.available_questions_2 = object?.available_questions_2 || 0;
		this.available_questions_3 = object?.available_questions_3 || 0;

		this.subcategories = (object?.subcategories || []).map((subObject) => ({
			id: subObject.id || 0,
			name: subObject.name || '',
			available_questions: subObject.available_questions || 0,
			available_questions_1: subObject.available_questions_1 || 0,
			available_questions_2: subObject.available_questions_2 || 0,
			available_questions_3: subObject.available_questions_3 || 0,
		}));
	}
}

export class CategoryGeneratorState {
	constructor(object?: Partial<CategoryGeneratorState>) {
		this.id = object?.id || 0;
		this.name = object?.name || '';
		this.available_questions = object?.available_questions || 0;
		this.available_questions_1 = object?.available_questions_1 || 0;
		this.available_questions_2 = object?.available_questions_2 || 0;
		this.available_questions_3 = object?.available_questions_3 || 0;
		this.levels = object?.levels || [1, 2, 3];
		this.question_count = object?.question_count || null;
		this.mode = object?.mode;
		this.invalid = object?.invalid || false;

		this.subcategories = (object?.subcategories || []).map((subObject) => ({
			id: subObject.id || 0,
			name: subObject.name || '',
			available_questions: subObject.available_questions || 0,
			available_questions_1: subObject.available_questions_1 || 0,
			available_questions_2: subObject.available_questions_2 || 0,
			available_questions_3: subObject.available_questions_3 || 0,
			levels: subObject.levels || [1, 2, 3],
			question_count: subObject.question_count || null,
			invalid: subObject.invalid || false,
		}));
	}
}
export interface CategoryGeneratorCartRequest {
	category: number;
	question_count: number | null;
	subcategories: SubcategoryGeneratorCartRequest[];
}

interface SubcategoryGeneratorCartRequest {
	subcategory: number;
	question_count: number | null;
	levels: number[];
}

/**
 * Canonical "how many questions does this category contribute" rule. The
 * payload transform, the configurator's totals/validation and the 409
 * availability re-check all define "submitted" through this one helper —
 * keep them in sync by changing it here only.
 */
export function getCategoryQuestionTotal(
	category: CategoryGeneratorState
): number {
	return (
		(category.question_count ?? 0) +
		category.subcategories.reduce(
			(sum, sub) => sum + (sub.question_count ?? 0),
			0
		)
	);
}

export function transformCategoryStateToCartRequest(
	categories: CategoryGeneratorState[]
): CategoryGeneratorCartRequest[] {
	return (
		categories
			// remove categories that contribute no questions (neither a category
			// count nor any subcategory counts)
			.filter((category) => getCategoryQuestionTotal(category) > 0)
			.map((category) => {
				const categoryCount = category.question_count ?? 0;
				// in category mode subcategories act as pool/level descriptors,
				// so null-count subs must be kept; in subcategory mode (no
				// category count) only the subs the user actually picked may be
				// sent — a leaked count-0 sub would reserve a stray question
				const filteredSubcategories = category.subcategories.filter(
					(subcategory) =>
						categoryCount > 0
							? subcategory.question_count !== 0
							: (subcategory.question_count ?? 0) > 0
				);

				return {
					category: category.id,
					question_count: category.question_count ?? 0,
					subcategories: filteredSubcategories.map((subcategory) => ({
						subcategory: subcategory.id,
						question_count: subcategory.question_count ?? 0,
						levels: subcategory.levels || [],
					})),
				};
			})
			// remove categories with no subcategories left
			.filter((category) => category.subcategories.length > 0)
	);
}

export function filterCategories(
	categories: CategoryGeneratorState[]
): CategoryGeneratorState[] {
	return categories
		.map((category) => {
			// Filter subcategories where available_questions > 0
			const filteredSubcategories: SubcategoryGeneratorState[] =
				category.subcategories.filter(
					(sub) => sub.available_questions && sub.available_questions > 0
				);

			// Return category only if available_questions > 0
			if (category.available_questions > 0) {
				return {
					...category,
					subcategories: filteredSubcategories,
				};
			}

			return null;
		})
		.filter(
			(category): category is CategoryGeneratorState => category !== null
		); // Remove null values
}

export enum EnumQuizType {
	none,
	quick,
	generator,
}
